import { debounce, isNil } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AddressFieldValue } from "../../types/fieldTypes";
import {
  FilteringEventArgs,
  SelectEventArgs,
} from "@syncfusion/ej2-react-dropdowns";

export type SmartAddressSuggestion = AddressFieldValue & Record<string, any>;

export const useSmartAddress = (
  getAddressesByQuery: (
    address: Partial<AddressFieldValue>
  ) => Promise<AddressFieldValue[]>,
  getAddressByQuery: (address: AddressFieldValue) => Promise<AddressFieldValue>,
  shouldSetCountyOnSelect: boolean,
  address: AddressFieldValue | undefined,
  onSubmit: ((address: AddressFieldValue) => void) | undefined,
  renderHasAutoSuggestToggle?: boolean,
  lookupSingleAddressOnSelect: boolean = false
) => {
  const [localAddress, setLocalAddress] = useState<
    AddressFieldValue | undefined
  >({
    ...address,
    hasAutoSuggest:
      address?.hasAutoSuggest !== undefined ? address.hasAutoSuggest : true,
  });

  const shouldPerformSearch = useMemo(() => {
    if (renderHasAutoSuggestToggle !== true) {
      return true;
    }

    return localAddress?.hasAutoSuggest !== false;
  }, [renderHasAutoSuggestToggle, localAddress?.hasAutoSuggest]);

  const [addressSuggestions, setAddressSuggestions] = useState<
    SmartAddressSuggestion[]
  >([]);
  const [isLoading, setIsLoading] = useState(false);
  const [clearCounter, setClearCounter] = useState(0);

  const getAddress = async (searchAddress: AddressFieldValue) => {
    const shouldGetAddress =
      searchAddress &&
      searchAddress.street &&
      searchAddress.street.length > 2 &&
      localAddress?.hasAutoSuggest;

    if (shouldGetAddress) {
      return getAddressByQuery(searchAddress);
    }
  };

  // Change Handlers
  const debouncedChangeHandler = useCallback(
    debounce(
      async (street: string, hasAutoSuggest: boolean) => {
        const shouldGetAddress = street.length > 2 && hasAutoSuggest === true;

        if (shouldGetAddress) {
          setIsLoading(true);

          const addresses = await getAddressesByQuery({ street });
          setAddressSuggestions(addresses);
          setIsLoading(false);

          // To get around type error.
          return addresses as unknown as SmartAddressSuggestion[];
        }
      },
      1000,
      {
        leading: true,
        trailing: false,
        maxWait: 1000,
      }
    ),
    [getAddressesByQuery, localAddress?.hasAutoSuggest, setAddressSuggestions, setIsLoading]
  );

  const onAddressChangeHandler = useCallback(
    (address: AddressFieldValue) => {
      setLocalAddress(address);
      onSubmit?.(address);
    },
    [onSubmit, setLocalAddress]
  );

  // Helper function to update address fields with change detection
  const updateAddressFieldInternal = useCallback(
    (updates: Partial<AddressFieldValue>, currentAddress: AddressFieldValue) => {
      // Check if any values actually changed before updating
      const hasChanges = Object.entries(updates).some(
        ([key, value]) =>
          currentAddress?.[key as keyof AddressFieldValue] !== value
      );

      if (hasChanges) {
        onAddressChangeHandler({
          ...currentAddress,
          ...updates,
        });
      }
    },
    [onAddressChangeHandler]
  );

  const updateAddressField = useCallback(
    (updates: Partial<AddressFieldValue>) => {
      if (localAddress) {
        updateAddressFieldInternal(updates, localAddress);
      }
    },
    [localAddress, updateAddressFieldInternal]
  );

  useEffect(() => {
    if (address && localAddress) {
      updateAddressFieldInternal(address, localAddress);
    }
  }, [address, updateAddressFieldInternal]);

  // Factory function for single field change handlers
  const createFieldChangeHandler = useCallback(
    (field: keyof AddressFieldValue) => (value: any) =>
      updateAddressField({ [field]: value }),
    [updateAddressField]
  );

  const onSelectAddressSuggestionHandler = useCallback(
    (address: AddressFieldValue) => {
      if (!shouldSetCountyOnSelect && !lookupSingleAddressOnSelect) {
        onAddressChangeHandler({
          ...address,
          hasAutoSuggest: localAddress?.hasAutoSuggest,
        });
        return;
      }

      setIsLoading(true);
      getAddress(address).then((addressDetail) => {
        if (addressDetail) {
          onAddressChangeHandler({
            ...addressDetail,
            hasAutoSuggest: localAddress?.hasAutoSuggest,
          });
        } else {
          onAddressChangeHandler({
            ...address,
            hasAutoSuggest: localAddress?.hasAutoSuggest,
          });
        }

        setIsLoading(false);
      });
    },
    [
      onAddressChangeHandler,
      localAddress?.hasAutoSuggest,
      shouldSetCountyOnSelect,
      lookupSingleAddressOnSelect,
      getAddress,
    ]
  );

  // Specialized handler for autocomplete changes
  const handleAutoCompleteChange = useCallback(
    (e: any) => {
      // If an item was selected from dropdown
      if (e.itemData && e.itemData.street !== localAddress?.street) {
        onSelectAddressSuggestionHandler(e.itemData as AddressFieldValue);
      }
      // If the field was cleared (no itemData and value is empty)
      else if (isNil(e.itemData) && (isNil(e.value) || e.value.trim() === "")) {
        setClearCounter((prev) => prev + 1);
        onAddressChangeHandler({
          street: "",
          street2: "",
          city: "",
          state: "",
          zip: "",
          county: "",
          fullAddress: "",
          hasAutoSuggest: localAddress?.hasAutoSuggest,
        });
      }
      // If the user typed their own address (no suggestion selected) and it's not empty
      else if (isNil(e.itemData) && e.value && e.value.trim() !== "") {
        onAddressChangeHandler({
          ...localAddress,
          street: e.value,
          fullAddress: e.value,
          hasAutoSuggest: localAddress?.hasAutoSuggest,
        });
      }
    },
    [
      localAddress?.street,
      localAddress?.fullAddress,
      localAddress?.hasAutoSuggest,
      onSelectAddressSuggestionHandler,
      onAddressChangeHandler,
      setClearCounter,
    ]
  );

  const handleFiltering = useCallback(
    async (e: FilteringEventArgs) => {
      if (e.text.length > 2 && shouldPerformSearch) {
        const result = await debouncedChangeHandler(
          e.text,
          shouldPerformSearch
        );
        if (result) {
          // Necessary to force the update of the data. Really, this is the cleanest way.
          e.updateData(result);
        }
      }
    },
    [debouncedChangeHandler, shouldPerformSearch]
  );

  const handleSelect = useCallback(
    (e: SelectEventArgs) => {
      onSelectAddressSuggestionHandler(e.itemData as AddressFieldValue);
    },
    [onSelectAddressSuggestionHandler]
  );

  const shouldDisableAutoSuggest = useMemo(() => {
    return renderHasAutoSuggestToggle && localAddress?.hasAutoSuggest === false;
  }, [renderHasAutoSuggestToggle, localAddress?.hasAutoSuggest]);

  return {
    debouncedChangeHandler,
    isLoading,
    addressSuggestions,
    localAddress,
    shouldDisableAutoSuggest,
    clearCounter,

    onAddressChangeHandler,
    onSelectAddressSuggestionHandler,
    handleFiltering,
    handleSelect,
    updateAddressField,
    createFieldChangeHandler,
    handleAutoCompleteChange,
  };
};
